import { QueryDocumentSnapshot } from 'firebase/firestore';
import { Resident } from '@/types/resources/resident';
import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  limit, 
  startAfter,
  setDoc,
  arrayUnion,
  QueryConstraint,
  addDoc,
  writeBatch,
  getCountFromServer,
  Timestamp
} from 'firebase/firestore';
import { removeUndefined } from '@/utils/removeUndefined';
import { Language, Currency, User, UserRole } from '@/types/users';
import { addWorkspaceAndActivity, createActivityRecord, createDetailedActivityRecord, isDemoWorkspace } from '@/utils/apiHelpers';
import type { ActivityType } from '@/types/common';
import { addUser, deleteUser, addUsersBulk } from './users';
import { formatFirestoreTimestamp } from '@/utils/date';
import { updateApartmentUnit } from './properties';
import { Package } from '@/types/residents/package';
import { Reservation } from '@/types/residents/reservation';
import { Visitor } from '@/types/residents/visitor';
import { loadMockData } from '@/mock';

// Helper function to filter mock data based on search term
function filterMockData<T extends { user?: { displayName?: string }; id?: string; status?: string; type?: string; block?: { id?: string }; apartmentUnit?: { id?: string } }>(
  data: T[],
  searchTerm?: string,
  status?: string,
  type?: string,
  blockId?: string,
  unitId?: string
): T[] {
  let filteredData = data;
  
  if (searchTerm) {
    filteredData = filteredData.filter(item => 
      item.user?.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.id?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }
  
  if (status) {
    filteredData = filteredData.filter(item => item.status === status);
  }
  
  if (type) {
    filteredData = filteredData.filter(item => item.type === type);
  }
  
  if (blockId) {
    filteredData = filteredData.filter(item => item.block?.id === blockId);
  }
  
  if (unitId) {
    filteredData = filteredData.filter(item => item.apartmentUnit?.id === unitId);
  }
  
  return filteredData;
}

// Helper function to simulate pagination for mock data
function paginateMockData<T>(
  data: T[],
  pageSize?: number,
  currentPage: number = 1
): { data: T[]; total: number } {
  if (!pageSize) {
    return { data, total: data.length };
  }

  // Calculate start index based on current page
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = data.slice(startIndex, endIndex);

  return {
    data: paginatedData,
    total: data.length
  };
}

// Constants
const RESIDENTS_COLLECTION = 'residents';
const PACKAGES_COLLECTION = 'packages';
const RESERVATIONS_COLLECTION = 'reservations';
const VISITORS_COLLECTION = 'visitors';

// Types
export interface ResidentsQueryParams {
  searchTerm?: string;
  status?: string;
  type?: string;
  blockId?: string;
  unitId?: string;
  pageSize?: number;
  page?: number; // Add page parameter
  lastDoc?: QueryDocumentSnapshot;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  firestoreUser: User;
}

export interface CreateResidentInput extends Omit<Resident, 'id' | 'user'> {
  user: {
    email: string;
    displayName: string;
    username: string;
    language: Language;
    currency: Currency;
    phoneNumber: string;
  };
  existingUser?: User; // Complete user object if found during email lookup
}

// Resident API
export async function getResidents({
  searchTerm,
  status,
  type,
  blockId,
  unitId,
  pageSize,
  page = 1, // Default to page 1
  lastDoc,
  sortBy,
  sortOrder,
  firestoreUser
}: ResidentsQueryParams): Promise<{ residents: Resident[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log('DEMO MODE: Fetching mock residents with filters', { searchTerm, status, blockId, page, pageSize });
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Use imported mock data
    const mockResidents = await loadMockData<Resident[]>('residents', firestoreUser.language);
    const filteredData = filterMockData(mockResidents, searchTerm, status, type, blockId, unitId);
    const paginatedData = paginateMockData(filteredData, pageSize, page);
    
    return {
      residents: paginatedData.data,
      lastDoc: undefined, // Mock data doesn't have Firestore documents
      total: paginatedData.total
    };
  }
  
  console.log('API Call: getResidents', { searchTerm, status, type, blockId, unitId, pageSize, lastDoc, sortBy, sortOrder, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const residentsRef = collection(db, RESIDENTS_COLLECTION);
    const filterConstraints: QueryConstraint[] = [
      where('workspace.id', '==', firestoreUser.workspace.id)
    ];

    if (searchTerm) {
      filterConstraints.push(
        where('user.displayName', '>=', searchTerm),
        where('user.displayName', '<=', searchTerm + '\uf8ff')
      );
    }

    if (status) {
      filterConstraints.push(where('status', '==', status));
    }

    if (type) {
      filterConstraints.push(where('type', '==', type));
    }

    if (blockId) {
      filterConstraints.push(where('block.id', '==', blockId));
    }

    if (unitId) {
      filterConstraints.push(where('apartmentUnit.id', '==', unitId));
    }

    let q = query(residentsRef, ...filterConstraints);

    if (pageSize) {
      q = query(q, limit(pageSize));
    }

    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const snapshot = await getDocs(q);
    const residents = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        moveInDate: formatFirestoreTimestamp(data.moveInDate),
        moveOutDate: formatFirestoreTimestamp(data.moveOutDate)
      } as Resident;
    });

    // Get total count using aggregation query
    const totalQuery = query(residentsRef, ...filterConstraints);
    const totalSnapshot = await getCountFromServer(totalQuery);
    const total = totalSnapshot.data().count;

    return {
      residents,
      lastDoc: snapshot.docs[snapshot.docs.length - 1],
      total: total
    };
  } catch (error) {
    console.error('Error fetching residents:', error);
    throw new Error('Failed to fetch residents');
  }
}

export async function getResidentById(id: string, firestoreUser: User): Promise<Resident | null> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log(`DEMO MODE: Fetching mock resident with id: ${id}`);
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Use imported mock data
    const mockResidents = await loadMockData<Resident[]>('residents', firestoreUser.language);
    const resident = mockResidents.find(r => r.id === id) || null;
    return resident;
  }
  
  console.log('API Call: getResidentById', { id, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Get the resident document directly by ID
    const residentRef = doc(db, RESIDENTS_COLLECTION, id);
    const residentDoc = await getDoc(residentRef);
    
    if (!residentDoc.exists()) {
      return null;
    }

    const residentData = residentDoc.data();
    
    // Check if the resident belongs to the current workspace
    if (residentData.workspace.id !== firestoreUser.workspace.id) {
      return null;
    }

    return {
      ...residentData,
      id: residentDoc.id,
      moveInDate: formatFirestoreTimestamp(residentData.moveInDate),
      moveOutDate: formatFirestoreTimestamp(residentData.moveOutDate)
    } as Resident;
  } catch (error) {
    console.error('Error fetching resident:', error);
    throw new Error('Failed to fetch resident');
  }
}

export async function addResident(resident: CreateResidentInput, firestoreUser: User): Promise<string> {
  console.log('API Call: addResident', { resident });
  try {
    let userToUse: User;

    if (resident.existingUser) {
      // Use existing user - update workspace and roles if needed
      const { updateUser } = await import('./users');
      const existingUser = resident.existingUser;
      
      // Prepare updates for the existing user
      const userUpdates: any = {};
      
      // If user doesn't have workspace, add it
      if (!existingUser.workspace) {
        userUpdates.workspace = firestoreUser.workspace;
        existingUser.workspace = firestoreUser.workspace;
      }

      // Add resident role if not already present
      if (!existingUser.roles.includes('resident')) {
        userUpdates.roles = arrayUnion('resident');
        existingUser.roles = [...existingUser.roles, 'resident'];
      }

      // Update user if there are changes
      if (Object.keys(userUpdates).length > 0) {
        await updateUser(existingUser.id, userUpdates, firestoreUser);
      }

      userToUse = existingUser;
      console.log('Using existing user:', userToUse);
    } else {
      // Create new user record
      const userData = {
        email: resident.user.email,
        displayName: resident.user.displayName,
        username: resident.user.username,
        status: 'active' as const,
        contacts: [],
        roles: ['resident' as UserRole],
        workspace: firestoreUser.workspace,
        language: resident.user.language,
        currency: resident.user.currency,
        phoneNumber: resident.user.phoneNumber,
        invited: false,
        accepted: false
      };

      userToUse = await addUser(userData);
      console.log('Created new user:', userToUse);
    }

    // Step 2: Create resident record with workspace and activity, referencing the user
    const residentData = addWorkspaceAndActivity(
      {
        ...resident,
        user: {
          id: userToUse.id,
          displayName: userToUse.displayName
        },
        apartmentUnit: resident.apartmentUnit ? {
          id: resident.apartmentUnit.id,
          number: resident.apartmentUnit.number
        } : undefined,
        block: resident.block ? {
          id: resident.block.id,
          name: resident.block.name
        } : undefined
      },
      firestoreUser,
      'created'
    );
    console.log('addResident activityRecord:', residentData.activityRecords[0]);
    console.log('addResident final residentData:', residentData);

    // Create resident record
    const residentRef = doc(collection(db, RESIDENTS_COLLECTION));
    await setDoc(residentRef, removeUndefined(residentData));
    const residentId = residentRef.id;

    // Step 3: Update the ApartmentUnit to include resident reference
    if (resident.apartmentUnit?.id) {
      try {
        await updateApartmentUnit(resident.apartmentUnit.id, {
          resident: {
            id: residentId,
            name: userToUse.displayName
          },
          status: 'occupied'
        }, firestoreUser);
        console.log('Updated apartment unit with resident reference');
      } catch (error) {
        console.error('Error updating apartment unit:', error);
        // Don't throw error here as resident was already created successfully
      }
    }

    return residentId;
  } catch (error) {
    console.error('Error adding resident:', error);
    throw new Error('Failed to add resident. Please try again.');
  }
}

export async function updateResident(
  id: string,
  data: Partial<Resident>,
  firestoreUser: User
): Promise<void> {
  console.log('API Call: updateResident', { id, data, firestoreUser });
  
  try {
    const docRef = doc(db, RESIDENTS_COLLECTION, id);
    const residentDoc = await getDoc(docRef);

    if (!residentDoc.exists()) {
      throw new Error('Resident not found');
    }

    const existingData = residentDoc.data() as Resident;
    let activityType: ActivityType = 'updated';
    if (data.invited === true) {
      activityType = 'invited';
    }
    
    const activityRecord = createDetailedActivityRecord(activityType, existingData, data, firestoreUser);

    // Remove activityRecords from data to avoid conflicts
    const { activityRecords, ...updateData } = data;
    
    // Prepare the update data without activityRecords
    const cleanData = removeUndefined(updateData);
    
    // Add activity record separately
    const updatePayload = {
      ...cleanData,
      activityRecords: arrayUnion(activityRecord)
    };

    await updateDoc(docRef, updatePayload);

    // Handle apartment unit changes
    const oldApartmentUnitId = existingData.apartmentUnit?.id;
    const newApartmentUnitId = data.apartmentUnit?.id;

    // If apartment unit changed, update both old and new units
    if (oldApartmentUnitId !== newApartmentUnitId) {
      // Remove resident reference from old apartment unit
      if (oldApartmentUnitId) {
        try {
          await updateApartmentUnit(oldApartmentUnitId, {
            resident: undefined,
            status: 'vacant'
          }, firestoreUser);
          console.log('Removed resident reference from old apartment unit');
        } catch (error) {
          console.error('Error removing resident reference from old apartment unit:', error);
        }
      }

      // Add resident reference to new apartment unit
      if (newApartmentUnitId && data.apartmentUnit) {
        try {
          await updateApartmentUnit(newApartmentUnitId, {
            resident: {
              id: id,
              name: existingData.user?.displayName || data.user?.displayName || 'Unknown'
            },
            status: 'occupied'
          }, firestoreUser);
          console.log('Added resident reference to new apartment unit');
        } catch (error) {
          console.error('Error adding resident reference to new apartment unit:', error);
        }
      }
    }
  } catch (error) {
    console.error('Error updating resident:', error);
    throw new Error('Failed to update resident');
  }
}

export async function deleteResident(
  id: string,
  firestoreUser: User
): Promise<void> {
  console.log('API Call: deleteResident', { id, firestoreUser });
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Get the resident document directly
    const residentRef = doc(db, RESIDENTS_COLLECTION, id);
    const residentDoc = await getDoc(residentRef);

    if (!residentDoc.exists()) {
      throw new Error('Resident not found');
    }

    const residentData = residentDoc.data();
    if (residentData.workspace.id !== firestoreUser.workspace.id) {
      throw new Error('Resident not found in current workspace');
    }

    // Check for dependencies before deletion
    const dependencies: { type: string; items: any[] }[] = [];

    // Check if resident has active packages
    const packagesRef = collection(db, PACKAGES_COLLECTION);
    const packagesQuery = query(
      packagesRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('resident.id', '==', id),
      where('status', 'in', ['received', 'picked_up'])
    );
    const packagesSnapshot = await getDocs(packagesQuery);
    
    if (!packagesSnapshot.empty) {
      const packages = packagesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      dependencies.push({
        type: 'packages',
        items: packages
      });
    }

    // Check if resident has active reservations
    const reservationsRef = collection(db, RESERVATIONS_COLLECTION);
    const reservationsQuery = query(
      reservationsRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('resident.id', '==', id),
      where('status', 'in', ['confirmed', 'pending'])
    );
    const reservationsSnapshot = await getDocs(reservationsQuery);
    
    if (!reservationsSnapshot.empty) {
      const reservations = reservationsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      dependencies.push({
        type: 'reservations',
        items: reservations
      });
    }

    // Check if resident has active visitors
    const visitorsRef = collection(db, VISITORS_COLLECTION);
    const visitorsQuery = query(
      visitorsRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('resident.id', '==', id),
      where('status', 'in', ['checked_in', 'pending'])
    );
    const visitorsSnapshot = await getDocs(visitorsQuery);
    
    if (!visitorsSnapshot.empty) {
      const visitors = visitorsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      dependencies.push({
        type: 'visitors',
        items: visitors
      });
    }

    // If there are dependencies, throw detailed error
    if (dependencies.length > 0) {
      const error = new Error('Cannot delete resident with dependencies');
      (error as any).dependencies = dependencies;
      (error as any).residentName = residentData.user?.displayName || residentData.id;
      throw error;
    }

    const activityRecord = createActivityRecord('deleted', residentData, firestoreUser);

    // Add the activity record before deletion
    await updateDoc(residentRef, {
      activityRecords: arrayUnion(activityRecord)
    });

    // Delete the resident document
    await deleteDoc(residentRef);

    // Delete the associated user document (if it exists)
    if (residentData.user && residentData.user.id) {
      try {
        // Check if user document exists before attempting to delete
        const userRef = doc(db, 'users', residentData.user.id);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          await deleteUser(residentData.user.id, firestoreUser);
          console.log('Deleted associated user account');
        } else {
          console.log('User account not found, skipping deletion');
        }
      } catch (error) {
        console.error('Error deleting user account:', error);
        // Don't throw error here as resident was already deleted successfully
        // Just log the error and continue
      }
    }

    // Remove resident reference from apartment unit
    if (residentData.apartmentUnit?.id) {
      try {
        await updateApartmentUnit(residentData.apartmentUnit.id, {
          resident: undefined,
          status: 'vacant'
        }, firestoreUser);
        console.log('Removed resident reference from apartment unit');
      } catch (error) {
        console.error('Error removing resident reference from apartment unit:', error);
        // Don't throw error here as resident was already deleted successfully
      }
    }
  } catch (error) {
    console.error('Error deleting resident:', error);
    throw error;
  }
}

export async function forceDeleteResident(
  id: string,
  firestoreUser: User
): Promise<void> {
  console.log('API Call: forceDeleteResident', { id, firestoreUser });
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Get the resident document directly
    const residentRef = doc(db, RESIDENTS_COLLECTION, id);
    const residentDoc = await getDoc(residentRef);

    if (!residentDoc.exists()) {
      throw new Error('Resident not found');
    }

    const residentData = residentDoc.data();
    if (residentData.workspace.id !== firestoreUser.workspace.id) {
      throw new Error('Resident not found in current workspace');
    }

    // Force delete - clean up all dependencies first
    const batch = writeBatch(db);

    // Delete all packages for this resident
    const packagesRef = collection(db, PACKAGES_COLLECTION);
    const packagesQuery = query(
      packagesRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('resident.id', '==', id)
    );
    const packagesSnapshot = await getDocs(packagesQuery);
    
    packagesSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // Delete all reservations for this resident
    const reservationsRef = collection(db, RESERVATIONS_COLLECTION);
    const reservationsQuery = query(
      reservationsRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('resident.id', '==', id)
    );
    const reservationsSnapshot = await getDocs(reservationsQuery);
    
    reservationsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // Delete all visitors for this resident
    const visitorsRef = collection(db, VISITORS_COLLECTION);
    const visitorsQuery = query(
      visitorsRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('resident.id', '==', id)
    );
    const visitorsSnapshot = await getDocs(visitorsQuery);
    
    visitorsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // Delete the associated user document (if it exists)
    if (residentData.user && residentData.user.id) {
      try {
        // Check if user document exists before attempting to delete
        const userRef = doc(db, 'users', residentData.user.id);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          await deleteUser(residentData.user.id, firestoreUser);
          console.log('Deleted associated user account');
        } else {
          console.log('User account not found, skipping deletion');
        }
      } catch (error) {
        console.error('Error deleting user account:', error);
        // Don't throw error here as resident was already deleted successfully
        // Just log the error and continue
      }
    }

    // Remove resident reference from apartment unit
    if (residentData.apartmentUnit?.id) {
      try {
        await updateApartmentUnit(residentData.apartmentUnit.id, {
          resident: undefined,
          status: 'vacant'
        }, firestoreUser);
        console.log('Removed resident reference from apartment unit');
      } catch (error) {
        console.error('Error removing resident reference from apartment unit:', error);
      }
    }

    const activityRecord = createActivityRecord('deleted', residentData, firestoreUser);

    // Add the activity record before deletion
    batch.update(residentRef, {
      activityRecords: arrayUnion(activityRecord)
    });

    // Delete the resident document
    batch.delete(residentRef);

    // Commit all changes
    await batch.commit();
    
    console.log('Force deleted resident and all dependencies');
  } catch (error) {
    console.error('Error force deleting resident:', error);
    throw error;
  }
}

// Additional helper functions
export async function getResidentsByBlock(blockId: string, firestoreUser: User): Promise<Resident[]> {
  if (isDemoWorkspace(firestoreUser)) {
    // Use imported mock data
    const mockResidents = await loadMockData<Resident[]>('residents', firestoreUser.language);
    return mockResidents.filter(resident => resident.block?.id === blockId);
  }
  const { residents } = await getResidents({ blockId, firestoreUser });
  return residents;
}

export async function getResidentsByUnit(unitId: string, firestoreUser: User): Promise<Resident[]> {
  const { residents } = await getResidents({ unitId, firestoreUser });
  return residents;
}

export async function getResidentsByStatus(status: string, firestoreUser: User): Promise<Resident[]> {
  if (isDemoWorkspace(firestoreUser)) {
    // Use imported mock data
    const mockResidents = await loadMockData<Resident[]>('residents', firestoreUser.language);
    return mockResidents.filter(resident => resident.status === status);
  }
  const { residents } = await getResidents({ status, firestoreUser });
  return residents;
}

// ===== PACKAGES API =====

export async function getPackages(
  pageSize?: number,
  lastDoc?: QueryDocumentSnapshot,
  firestoreUser?: User
): Promise<{ packages: Package[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log('Demo mode: Returning mock packages');
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Import mock data dynamically to avoid circular dependencies
    const packages = await loadMockData<Package[]>('packages', firestoreUser!.language);
    
    // Sort by received date (newest first)
    const sortedPackages = packages.sort((a, b) => 
      (b.receivedAt?.getTime() ?? 0) - (a.receivedAt?.getTime() ?? 0)
    );
    
    // Apply pagination
    const paginatedData = paginateMockData(sortedPackages, pageSize, 1);
    
    return {
      packages: paginatedData.data,
      lastDoc: undefined,
      total: paginatedData.total
    };
  }

  console.log('API Call: getPackages', { pageSize, lastDoc, firestoreUser });
  
  try {
    const packagesRef = collection(db, PACKAGES_COLLECTION);
    let q = query(packagesRef);
    
    // Add workspace filter if user is provided
    if (firestoreUser?.workspace?.id) {
      q = query(q, where('workspace.id', '==', firestoreUser.workspace.id));
    }
    
    if (pageSize) {
      q = query(q, limit(pageSize));
    }
    
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }
    
    const snapshot = await getDocs(q);
    const packages: Package[] = [];
    
    snapshot.forEach(doc => {
      const data = doc.data();
      packages.push({
        id: doc.id,
        ...data,
        receivedAt: data.receivedAt?.toDate(),
        collectedAt: data.collectedAt?.toDate(),
        expiryDate: data.expiryDate?.toDate(),
      } as Package);
    });
    
    // Sort in memory instead of in query
    packages.sort((a, b) => (b.receivedAt?.getTime() ?? 0) - (a.receivedAt?.getTime() ?? 0));
    
    const lastVisible = snapshot.docs[snapshot.docs.length - 1];
    
    return {
      packages,
      lastDoc: lastVisible,
      total: packages.length
    };
  } catch (error) {
    console.error('Error fetching packages:', error);
    throw new Error('Failed to fetch packages');
  }
}

export async function getPackageById(id: string, firestoreUser?: User): Promise<Package | null> {
  try {
    console.log('API Call: getPackageById', { id, firestoreUser });
    const docRef = doc(db, PACKAGES_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      
      // Check workspace access if user is provided
      if (firestoreUser?.workspace?.id && data.workspace?.id !== firestoreUser.workspace.id) {
        return null;
      }
      
      return {
        id: docSnap.id,
        ...data,
        receivedAt: data.receivedAt?.toDate(),
        collectedAt: data.collectedAt?.toDate(),
        expiryDate: data.expiryDate?.toDate(),
      } as Package;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching package:', error);
    throw new Error('Failed to fetch package');
  }
}

export async function addPackage(packageData: Omit<Package, 'id'>, firestoreUser: User): Promise<string> {
  console.log('API Call: addPackage', { packageData, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Add workspace and activity record
    const packageWithWorkspace = addWorkspaceAndActivity(
      packageData,
      firestoreUser,
      'created'
    );

    // Clean the data to remove undefined values
    const cleanData = removeUndefined(packageWithWorkspace);

    const packagesRef = collection(db, PACKAGES_COLLECTION);
    const docRef = await addDoc(packagesRef, {
      ...cleanData,
      receivedAt: Timestamp.fromDate(packageData.receivedAt),
      collectedAt: packageData.collectedAt ? Timestamp.fromDate(packageData.collectedAt) : null,
      expiryDate: packageData.expiryDate ? Timestamp.fromDate(packageData.expiryDate) : null,
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error adding package:', error);
    throw new Error('Failed to add package');
  }
}

export async function updatePackage(id: string, data: Partial<Package>, firestoreUser: User): Promise<void> {
  console.log('API Call: updatePackage', { id, data, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const docRef = doc(db, PACKAGES_COLLECTION, id);
    const packageDoc = await getDoc(docRef);

    if (!packageDoc.exists()) {
      throw new Error('Package not found');
    }

    const existingData = packageDoc.data();
    
    // Check workspace access
    if (existingData.workspace?.id !== firestoreUser.workspace.id) {
      throw new Error('Package not found in current workspace');
    }

    const activityRecord = createDetailedActivityRecord('updated', existingData, data, firestoreUser);

    // Remove activityRecords from data to avoid conflicts
    const { activityRecords, receivedAt, collectedAt, expiryDate, ...updateData } = data;
    
    // Prepare the update data without activityRecords and timestamps
    const cleanData = removeUndefined(updateData);
    
    // Add activity record separately
    const updatePayload: any = {
      ...cleanData,
      activityRecords: arrayUnion(activityRecord)
    };

    // Handle timestamp conversions
    if (receivedAt) {
      updatePayload.receivedAt = Timestamp.fromDate(receivedAt);
    }
    if (collectedAt) {
      updatePayload.collectedAt = Timestamp.fromDate(collectedAt);
    }
    if (expiryDate) {
      updatePayload.expiryDate = Timestamp.fromDate(expiryDate);
    }
    
    await updateDoc(docRef, updatePayload);
  } catch (error) {
    console.error('Error updating package:', error);
    throw new Error('Failed to update package');
  }
}

export async function deletePackage(id: string, firestoreUser: User): Promise<void> {
  console.log('API Call: deletePackage', { id, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const docRef = doc(db, PACKAGES_COLLECTION, id);
    const packageDoc = await getDoc(docRef);

    if (!packageDoc.exists()) {
      throw new Error('Package not found');
    }

    const packageData = packageDoc.data();
    
    // Check workspace access
    if (packageData.workspace?.id !== firestoreUser.workspace.id) {
      throw new Error('Package not found in current workspace');
    }

    const activityRecord = createActivityRecord('deleted', packageData, firestoreUser);

    // Add the activity record before deletion
    await updateDoc(docRef, {
      activityRecords: arrayUnion(activityRecord)
    });

    // Delete the package document
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting package:', error);
    throw new Error('Failed to delete package');
  }
}

// ===== RESERVATIONS API =====

export async function getReservations(
  pageSize?: number,
  lastDoc?: QueryDocumentSnapshot,
  firestoreUser?: User,
  searchTerm?: string,
  page: number = 1
): Promise<{ reservations: Reservation[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log('Demo mode: Returning mock reservations');
    await new Promise(resolve => setTimeout(resolve, 300));

    // Load language-specific mock data
    const reservations = await loadMockData<Reservation[]>('reservations', firestoreUser!.language);
    
    // Filter mock data based on search term
    let filteredData = reservations;
    if (searchTerm) {
      filteredData = reservations.filter((reservation: Reservation) =>
        reservation.amenity.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reservation.purpose.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (reservation.resident?.name && reservation.resident.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (reservation.employee?.name && reservation.employee.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (reservation.vendor?.name && reservation.vendor.name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    const paginatedData = paginateMockData(filteredData, pageSize, page);

    return {
      reservations: paginatedData.data,
      lastDoc: undefined,
      total: paginatedData.total
    };
  }
  
  console.log('API Call: getReservations', { pageSize, lastDoc, firestoreUser });
  
  try {
    const reservationsRef = collection(db, RESERVATIONS_COLLECTION);
    let q = query(reservationsRef);
    
    // Add workspace filter if user is provided
    if (firestoreUser?.workspace?.id) {
      q = query(q, where('workspace.id', '==', firestoreUser.workspace.id));
    }
    
    if (pageSize) {
      q = query(q, limit(pageSize));
    }
    
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }
    
    const snapshot = await getDocs(q);
    const reservations: Reservation[] = [];
    
    snapshot.forEach(doc => {
      const data = doc.data();
      reservations.push({
        id: doc.id,
        ...data,
        startTime: data.startTime?.toDate(),
        endTime: data.endTime?.toDate(),
      } as Reservation);
    });
    
    // Sort in memory instead of in query
    reservations.sort((a, b) => (b.startTime?.getTime() ?? 0) - (a.startTime?.getTime() ?? 0));
    
    const lastVisible = snapshot.docs[snapshot.docs.length - 1];
    
    return {
      reservations,
      lastDoc: lastVisible,
      total: reservations.length
    };
  } catch (error) {
    console.error('Error fetching reservations:', error);
    throw new Error('Failed to fetch reservations');
  }
}

export async function getReservationById(id: string, firestoreUser?: User): Promise<Reservation | null> {
  try {
    console.log('API Call: getReservationById', { id, firestoreUser });
    const docRef = doc(db, RESERVATIONS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      
      // Check workspace access if user is provided
      if (firestoreUser?.workspace?.id && data.workspace?.id !== firestoreUser.workspace.id) {
        return null;
      }
      
      return {
        id: docSnap.id,
        ...data,
        startTime: data.startTime?.toDate(),
        endTime: data.endTime?.toDate(),
      } as Reservation;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching reservation:', error);
    throw new Error('Failed to fetch reservation');
  }
}

export async function addReservation(reservationData: Omit<Reservation, 'id'>, firestoreUser: User): Promise<string> {
  console.log('API Call: addReservation', { reservationData, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Add workspace and activity record
    const reservationWithWorkspace = addWorkspaceAndActivity(
      reservationData,
      firestoreUser,
      'created'
    );

    // Clean the data to remove undefined values
    const cleanData = removeUndefined(reservationWithWorkspace);

    const reservationsRef = collection(db, RESERVATIONS_COLLECTION);
    const docRef = await addDoc(reservationsRef, {
      ...cleanData,
      startTime: Timestamp.fromDate(reservationData.startTime),
      endTime: Timestamp.fromDate(reservationData.endTime),
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error adding reservation:', error);
    throw new Error('Failed to add reservation');
  }
}

export async function updateReservation(id: string, data: Partial<Reservation>, firestoreUser: User): Promise<void> {
  console.log('API Call: updateReservation', { id, data, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const docRef = doc(db, RESERVATIONS_COLLECTION, id);
    const reservationDoc = await getDoc(docRef);

    if (!reservationDoc.exists()) {
      throw new Error('Reservation not found');
    }

    const existingData = reservationDoc.data();
    
    // Check workspace access
    if (existingData.workspace?.id !== firestoreUser.workspace.id) {
      throw new Error('Reservation not found in current workspace');
    }

    const activityRecord = createDetailedActivityRecord('updated', existingData, data, firestoreUser);

    // Remove activityRecords from data to avoid conflicts
    const { activityRecords, startTime, endTime, ...updateData } = data;
    
    // Prepare the update data without activityRecords and timestamps
    const cleanData = removeUndefined(updateData);
    
    // Add activity record separately
    const updatePayload: any = {
      ...cleanData,
      activityRecords: arrayUnion(activityRecord)
    };

    // Handle timestamp conversions
    if (startTime) {
      updatePayload.startTime = Timestamp.fromDate(startTime);
    }
    if (endTime) {
      updatePayload.endTime = Timestamp.fromDate(endTime);
    }
    
    await updateDoc(docRef, updatePayload);
  } catch (error) {
    console.error('Error updating reservation:', error);
    throw new Error('Failed to update reservation');
  }
}

export async function deleteReservation(id: string, firestoreUser: User): Promise<void> {
  console.log('API Call: deleteReservation', { id, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const docRef = doc(db, RESERVATIONS_COLLECTION, id);
    const reservationDoc = await getDoc(docRef);

    if (!reservationDoc.exists()) {
      throw new Error('Reservation not found');
    }

    const reservationData = reservationDoc.data();
    
    // Check workspace access
    if (reservationData.workspace?.id !== firestoreUser.workspace.id) {
      throw new Error('Reservation not found in current workspace');
    }

    const activityRecord = createActivityRecord('deleted', reservationData, firestoreUser);

    // Add the activity record before deletion
    await updateDoc(docRef, {
      activityRecords: arrayUnion(activityRecord)
    });

    // Delete the reservation document
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting reservation:', error);
    throw new Error('Failed to delete reservation');
  }
}

// ===== VISITORS API =====

export async function getVisitors(
  pageSize?: number,
  lastDoc?: QueryDocumentSnapshot,
  firestoreUser?: User,
  searchTerm?: string,
  page: number = 1
): Promise<{ visitors: Visitor[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log('Demo mode: Returning mock visitors');
    await new Promise(resolve => setTimeout(resolve, 300));

    // Load language-specific mock data
    const visitors = await loadMockData<Visitor[]>('visitors', firestoreUser!.language);
    
    // Filter mock data based on search term
    let filteredData = visitors;
    if (searchTerm) {
      filteredData = visitors.filter((visitor: Visitor) =>
        visitor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        visitor.resident?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (visitor.phone && visitor.phone.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (visitor.email && visitor.email.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    const paginatedData = paginateMockData(filteredData, pageSize, page);

    return {
      visitors: paginatedData.data,
      lastDoc: undefined,
      total: paginatedData.total
    };
  }
  
  console.log('API Call: getVisitors', { pageSize, lastDoc, firestoreUser });
  
  try {
    const visitorsRef = collection(db, VISITORS_COLLECTION);
    let q = query(visitorsRef);
    
    // Add workspace filter if user is provided
    if (firestoreUser?.workspace?.id) {
      q = query(q, where('workspace.id', '==', firestoreUser.workspace.id));
    }
    
    if (pageSize) {
      q = query(q, limit(pageSize));
    }
    
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }
    
    const snapshot = await getDocs(q);
    const visitors: Visitor[] = [];
    
    snapshot.forEach(doc => {
      const data = doc.data();
      visitors.push({
        id: doc.id,
        ...data,
        expectedArrival: data.expectedArrival?.toDate(),
        expectedDeparture: data.expectedDeparture?.toDate(),
        actualArrival: data.actualArrival?.toDate(),
        actualDeparture: data.actualDeparture?.toDate(),
      } as Visitor);
    });
    
    // Sort in memory instead of in query
    visitors.sort((a, b) => (b.expectedArrival?.getTime() ?? 0) - (a.expectedArrival?.getTime() ?? 0));
    
    const lastVisible = snapshot.docs[snapshot.docs.length - 1];
    
    return {
      visitors,
      lastDoc: lastVisible,
      total: visitors.length
    };
  } catch (error) {
    console.error('Error fetching visitors:', error);
    throw new Error('Failed to fetch visitors');
  }
}

export async function getVisitorById(id: string, firestoreUser?: User): Promise<Visitor | null> {
  try {
    console.log('API Call: getVisitorById', { id, firestoreUser });
    const docRef = doc(db, VISITORS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      
      // Check workspace access if user is provided
      if (firestoreUser?.workspace?.id && data.workspace?.id !== firestoreUser.workspace.id) {
        return null;
      }
      
      return {
        id: docSnap.id,
        ...data,
        expectedArrival: data.expectedArrival?.toDate(),
        expectedDeparture: data.expectedDeparture?.toDate(),
        actualArrival: data.actualArrival?.toDate(),
        actualDeparture: data.actualDeparture?.toDate(),
      } as Visitor;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching visitor:', error);
    throw new Error('Failed to fetch visitor');
  }
}

export async function addVisitor(visitorData: Omit<Visitor, 'id'>, firestoreUser: User): Promise<string> {
  console.log('API Call: addVisitor', { visitorData, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Add workspace and activity record
    const visitorWithWorkspace = addWorkspaceAndActivity(
      visitorData,
      firestoreUser,
      'created'
    );

    // Clean the data to remove undefined values
    const cleanData = removeUndefined(visitorWithWorkspace);

    const visitorsRef = collection(db, VISITORS_COLLECTION);
    const docRef = await addDoc(visitorsRef, {
      ...cleanData,
      expectedArrival: Timestamp.fromDate(visitorData.expectedArrival),
      expectedDeparture: Timestamp.fromDate(visitorData.expectedDeparture),
      actualArrival: visitorData.actualArrival ? Timestamp.fromDate(visitorData.actualArrival) : null,
      actualDeparture: visitorData.actualDeparture ? Timestamp.fromDate(visitorData.actualDeparture) : null,
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error adding visitor:', error);
    throw new Error('Failed to add visitor');
  }
}

export async function updateVisitor(id: string, data: Partial<Visitor>, firestoreUser: User): Promise<void> {
  console.log('API Call: updateVisitor', { id, data, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const docRef = doc(db, VISITORS_COLLECTION, id);
    const visitorDoc = await getDoc(docRef);

    if (!visitorDoc.exists()) {
      throw new Error('Visitor not found');
    }

    const existingData = visitorDoc.data();
    
    // Check workspace access
    if (existingData.workspace?.id !== firestoreUser.workspace.id) {
      throw new Error('Visitor not found in current workspace');
    }

    const activityRecord = createDetailedActivityRecord('updated', existingData, data, firestoreUser);

    // Remove activityRecords from data to avoid conflicts
    const { activityRecords, expectedArrival, expectedDeparture, actualArrival, actualDeparture, ...updateData } = data;
    
    // Prepare the update data without activityRecords and timestamps
    const cleanData = removeUndefined(updateData);
    
    // Add activity record separately
    const updatePayload: any = {
      ...cleanData,
      activityRecords: arrayUnion(activityRecord)
    };

    // Handle timestamp conversions
    if (expectedArrival) {
      updatePayload.expectedArrival = Timestamp.fromDate(expectedArrival);
    }
    if (expectedDeparture) {
      updatePayload.expectedDeparture = Timestamp.fromDate(expectedDeparture);
    }
    if (actualArrival) {
      updatePayload.actualArrival = Timestamp.fromDate(actualArrival);
    }
    if (actualDeparture) {
      updatePayload.actualDeparture = Timestamp.fromDate(actualDeparture);
    }
    
    await updateDoc(docRef, updatePayload);
  } catch (error) {
    console.error('Error updating visitor:', error);
    throw new Error('Failed to update visitor');
  }
}

export async function deleteVisitor(id: string, firestoreUser: User): Promise<void> {
  console.log('API Call: deleteVisitor', { id, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const docRef = doc(db, VISITORS_COLLECTION, id);
    const visitorDoc = await getDoc(docRef);

    if (!visitorDoc.exists()) {
      throw new Error('Visitor not found');
    }

    const visitorData = visitorDoc.data();
    
    // Check workspace access
    if (visitorData.workspace?.id !== firestoreUser.workspace.id) {
      throw new Error('Visitor not found in current workspace');
    }

    const activityRecord = createActivityRecord('deleted', visitorData, firestoreUser);

    // Add the activity record before deletion
    await updateDoc(docRef, {
      activityRecords: arrayUnion(activityRecord)
    });

    // Delete the visitor document
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting visitor:', error);
    throw new Error('Failed to delete visitor');
  }
}

// Bulk upload residents using Firestore batch operations
export async function addResidentsBulk(
  residents: Omit<Resident, 'id' | 'activityRecords' | 'workspace'>[], 
  firestoreUser: User,
  onProgress?: (uploaded: number, total: number) => void
): Promise<{ successCount: number; errorCount: number; errors: string[] }> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate progress for demo mode
    for (let i = 0; i < residents.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 100));
      onProgress?.(i + 1, residents.length);
    }
    return { successCount: residents.length, errorCount: 0, errors: [] };
  }
  
  try {
    const CHUNK_SIZE = 500; // Firestore batch limit
    const chunks = [];
    
    // Split records into chunks of 500
    for (let i = 0; i < residents.length; i += CHUNK_SIZE) {
      chunks.push(residents.slice(i, i + CHUNK_SIZE));
    }

    let totalUploaded = 0;
    const errors: string[] = [];
    
    // Process each chunk with progress updates
    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      const chunk = chunks[chunkIndex];
      const batch = writeBatch(db);
      
      chunk.forEach((residentData) => {
        const residentRef = doc(collection(db, RESIDENTS_COLLECTION));
        const residentWithMetadata = addWorkspaceAndActivity(residentData, firestoreUser, 'created');
        const cleanedResident = removeUndefined(residentWithMetadata);
        
        batch.set(residentRef, cleanedResident);
      });

      try {
        await batch.commit();
        totalUploaded += chunk.length;
        onProgress?.(totalUploaded, residents.length);
      } catch (error) {
        console.error(`Error uploading chunk ${chunkIndex + 1}:`, error);
        errors.push(`Chunk ${chunkIndex + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    const successCount = totalUploaded;
    const errorCount = residents.length - totalUploaded;

    return { successCount, errorCount, errors };
  } catch (error) {
    console.error('Error in bulk upload:', error);
    throw new Error('Failed to upload residents in bulk');
  }
}

// Bulk upload residents with users using three-phase batch process
export async function addResidentsBulkWithUsers(
  residents: Omit<Resident, 'id' | 'activityRecords' | 'workspace'>[], 
  firestoreUser: User,
  onProgress?: (phase: 'users' | 'residents' | 'apartmentUnits', uploaded: number, total: number) => void
): Promise<{ successCount: number; errorCount: number; errors: string[] }> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate progress for demo mode
    for (let i = 0; i < residents.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 100));
      onProgress?.('users', i + 1, residents.length);
    }
    for (let i = 0; i < residents.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 100));
      onProgress?.('residents', i + 1, residents.length);
    }
    for (let i = 0; i < residents.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 100));
      onProgress?.('apartmentUnits', i + 1, residents.length);
    }
    return { successCount: residents.length, errorCount: 0, errors: [] };
  }
  
  try {
    // Phase 1: Extract and create all users
    const userData = residents.map(resident => {
      if (!resident.user || !resident.user.email || !resident.user.displayName || !resident.user.phoneNumber) {
        throw new Error('Resident user data is required and must include email, displayName, and phoneNumber');
      }
      return {
        email: resident.user.email,
        displayName: resident.user.displayName,
        phoneNumber: resident.user.phoneNumber,
        username: resident.user.email.split('@')[0], // Generate username from email
        language: 'en' as Language, // Default language
        currency: 'USD' as Currency, // Default currency
        status: 'active' as const,
        roles: ['resident' as UserRole],
        workspace: firestoreUser.workspace,
        contacts: [],
        invited: false,
        accepted: false
      };
    });

    console.log('Phase 1: Creating users in bulk...');
    const userResult = await addUsersBulk(userData, firestoreUser, (uploaded, total) => {
      onProgress?.('users', uploaded, total);
    });

    if (userResult.successCount !== residents.length) {
      throw new Error(`Only created ${userResult.successCount} out of ${residents.length} users. Errors: ${userResult.errors.join(', ')}`);
    }

    console.log('Phase 2: Creating residents with user references...');
    
    // Phase 2: Create residents with user references
    const CHUNK_SIZE = 500; // Firestore batch limit
    const chunks = [];
    
    // Split records into chunks of 500
    for (let i = 0; i < residents.length; i += CHUNK_SIZE) {
      chunks.push(residents.slice(i, i + CHUNK_SIZE));
    }

    let totalUploaded = 0;
    const errors: string[] = [];
    const createdResidents: Array<{ id: string; apartmentUnitId?: string; residentName: string }> = [];
    
    // Process each chunk with progress updates
    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      const chunk = chunks[chunkIndex];
      const batch = writeBatch(db);
      const residentRefs: any[] = [];
      
      chunk.forEach((residentData, index) => {
        const userIndex = totalUploaded + index;
        const user = userResult.createdUsers[userIndex];
        
        if (!user) {
          errors.push(`No user found for resident ${userIndex}`);
          return;
        }

        const residentRef = doc(collection(db, RESIDENTS_COLLECTION));
        residentRefs.push(residentRef);
        
        const residentWithUser = {
          ...residentData,
          user: {
            id: user.id,
            displayName: user.displayName
          }
        };
        
        const residentWithMetadata = addWorkspaceAndActivity(residentWithUser, firestoreUser, 'created');
        const cleanedResident = removeUndefined(residentWithMetadata);
        
        batch.set(residentRef, cleanedResident);
      });

      try {
        await batch.commit();
        
        // Collect resident IDs and apartment unit info for Phase 3
        residentRefs.forEach((residentRef, index) => {
          const residentData = chunk[index];
          if (residentData && residentData.apartmentUnit?.id) {
            createdResidents.push({
              id: residentRef.id,
              apartmentUnitId: residentData.apartmentUnit.id,
              residentName: userResult.createdUsers[totalUploaded + index].displayName
            });
          }
        });
        
        totalUploaded += chunk.length;
        onProgress?.('residents', totalUploaded, residents.length);
      } catch (error) {
        console.error(`Error uploading resident chunk ${chunkIndex + 1}:`, error);
        errors.push(`Resident chunk ${chunkIndex + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    console.log('Phase 3: Updating apartment units with resident references...');
    
    // Phase 3: Update apartment units with resident references
    const apartmentUnitUpdates = createdResidents.map(resident => ({
      apartmentUnitId: resident.apartmentUnitId!,
      resident: {
        id: resident.id,
        name: resident.residentName
      }
    }));
    
    let apartmentUnitUpdateCount = 0;
    
    // Process apartment unit updates in chunks
    const APARTMENT_UNIT_CHUNK_SIZE = 500;
    const apartmentUnitChunks = [];
    
    for (let i = 0; i < apartmentUnitUpdates.length; i += APARTMENT_UNIT_CHUNK_SIZE) {
      apartmentUnitChunks.push(apartmentUnitUpdates.slice(i, i + APARTMENT_UNIT_CHUNK_SIZE));
    }
    
    for (let chunkIndex = 0; chunkIndex < apartmentUnitChunks.length; chunkIndex++) {
      const chunk = apartmentUnitChunks[chunkIndex];
      const batch = writeBatch(db);
      
      chunk.forEach((update) => {
        const apartmentUnitRef = doc(db, 'apartment_units', update.apartmentUnitId);
        batch.update(apartmentUnitRef, {
          resident: update.resident,
          status: 'occupied' // Update status to occupied
        });
      });
      
      try {
        await batch.commit();
        apartmentUnitUpdateCount += chunk.length;
        onProgress?.('apartmentUnits', apartmentUnitUpdateCount, apartmentUnitUpdates.length);
      } catch (error) {
        console.error(`Error updating apartment unit chunk ${chunkIndex + 1}:`, error);
        errors.push(`Apartment unit chunk ${chunkIndex + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    const successCount = totalUploaded;
    const errorCount = residents.length - totalUploaded;

    return { successCount, errorCount, errors };
  } catch (error) {
    console.error('Error in bulk upload with users:', error);
    throw new Error('Failed to upload residents with users in bulk');
  }
} 